import { CalendarResolver } from '@frontend/sports/calendar/feature/resolvers';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { use } from '@frontend/sports/common/core/utils/router';
import { SportListOverviewResolver } from '@frontend/sports/sport-list/feature/resolvers';
import { WidgetResolver, WidgetSchemaMarkupResolver } from '@frontend/sports/widget/core/feature';
import { WidgetLayoutComponent } from '@frontend/sports/widget/feature/layout';

import { gameLauncherResolver } from '../banner/game-launcher.resolver';
import { CalendarBreadcrumbsResolver } from '../calendar/calendar-breadcrumbs.resolver';
import { providePromotionsMapIntegration } from '../crm-promotion/promotions-map-integration.provider';
import { HomeBreadcrumbsResolver } from '../home';
import { MainComponent } from '../main/main.component';
import { MultiSportsBreadcrumbsResolver } from '../multi-sports';
import { SitemapRoutingToken } from '../sitemap/sitemap-routing';
import { StandingsPageResolver } from '../standings-page/standings-page.resolver';
import { SportSubNavigationResolve } from '../sub-navigation/sport-sub-navigation/sport-sub-navigation.resolve';
import { INTERNAL_ESPORTS_ROUTES } from './esports-web-internal.routes';
import { SportRoute } from './multi-route.model';
import { MultiSportsHubGuard } from './multi-sports-hub.guard';
import { multiNameIdentifierMatcher } from './name-identifier.matcher';
import { NotAuthenticatedGuard } from './not-authenticated-guard.service';
import { OutsideSportsGuard } from './outside-sports.guard';
import { regexMatcher } from './regex.matcher';
import { SportRedirectGuard } from './sport-redirect.guard';

export const INTERNAL_SPORTS_WEB_ROUTES: SportRoute[] = [
    {
        path: '',
        data: {
            headerNavigation: true,
            tag: RouteTag.Lobby,
            sitemap: SitemapRoutingToken.SitemapHomeResolver,
        },
        component: MainComponent,
        canActivateChild: [NotAuthenticatedGuard],
        runGuardsAndResolvers: 'paramsChange',
        children: [
            {
                path: '',
                pathMatch: 'full',
                component: WidgetLayoutComponent,
                resolve: use(WidgetResolver),
                data: {
                    tag: RouteTag.Lobby,
                    breadcrumbs: HomeBreadcrumbsResolver,
                    schemaMarkup: WidgetSchemaMarkupResolver,
                },
            },
            {
                path: 'p',
                children: [
                    {
                        path: '**',
                        loadComponent: () => import('@frontend/sports/common/ui/empty-component').then((m) => m.EmptyComponent),
                        canActivate: [OutsideSportsGuard],
                    },
                ],
            },
            {
                path: 'find',
                providers: [providePromotionsMapIntegration()],
                data: {
                    headerNavigation: false,
                },
                loadChildren: () => import('../find-menu-integration/find-menu.routes').then((f) => f.FIND_MENU_INTEGRATION_ROUTES),
            },
            {
                path: 'affiliate-account-linking',
                loadChildren: () => import('../link-external-accounts/link-external-accounts.routes').then((r) => r.LINK_EXTERNAL_ACCOUNTS_ROUTES),
                data: {
                    authorized: true,
                },
            },
            {
                path: '{multiSports}',
                canActivateChild: [MultiSportsHubGuard],
                children: [
                    {
                        path: '',
                        redirectTo: '{multiSportsHubName}',
                        pathMatch: 'full',
                    },
                    {
                        path: '{multiSportsHubName}',
                        component: WidgetLayoutComponent,
                        resolve: use(WidgetResolver),
                        data: {
                            tag: RouteTag.MultiSportsLobby,
                            breadcrumbs: MultiSportsBreadcrumbsResolver,
                            headerNavigation: true,
                            schemaMarkup: WidgetSchemaMarkupResolver,
                            sitemap: SitemapRoutingToken.MultiSportsHubSitemapResolver,
                        },
                    },
                ],
            },
            {
                path: '{settings}',
                loadChildren: () => import('../settings/settings.routes').then((r) => r.SETTINGS_ROUTES),
            },
            {
                path: '{favourites}',
                //load styles
                loadChildren: () => import('../favourites-dashboard/favourites-dashboard.routes').then((r) => r.FAVOURITES_DASHBOARD_ROUTES),
                data: { shouldKeepSubNav: true },
            },
            {
                path: '{events}',
                loadChildren: () => import('../event-details/event-details.routes').then((r) => r.EVENT_DETAILS_ROUTES),
            },
            {
                path: '{live}',
                loadChildren: () => import('../event-list-live/live.routes').then((r) => r.LIVE_ROUTES),
            },
            {
                path: '{virtual}',
                loadChildren: () => import('../virtual-list/virtual-list.routes').then((r) => r.VIRTUAL_LIST_ROUTES),
            },
            {
                path: '{myBets}',
                loadChildren: () => import('../my-bets/my-bets.routes').then((r) => r.MY_BETS_ROUTES),
                data: { shouldKeepSubNav: true },
            },
            {
                path: '{all}',
                data: {
                    tag: RouteTag.SportList,
                },
                loadComponent: () => import('../sport-list/sport-list-overview.component').then((c) => c.SportListOverviewComponent),
                resolve: use(SportListOverviewResolver),
            },
            {
                path: '{games}/:name/:section',
                children: [
                    {
                        path: '',
                        loadComponent: () => import('../banner/game-launcher.component').then((c) => c.GameLauncherComponent),
                        data: { authorized: true, headerNavigation: false },
                        resolve: { model: gameLauncherResolver },
                    },
                    {
                        path: ':position/:title/:provider/:lobbyPosition',
                        loadComponent: () => import('../banner/game-launcher.component').then((c) => c.GameLauncherComponent),
                        data: { authorized: true, headerNavigation: false },
                        resolve: { model: gameLauncherResolver },
                    },
                ],
            },
            {
                matcher: regexMatcher,
                data: {
                    matcherPattern: String.raw`^((.*)-)?(\d+)/{fixtures}/((.*)-)?(\d:\d+)$`,
                    matcherMode: 'full',
                },
                loadChildren: () => import('../racing/racing.routes').then((r) => r.RACING_ROUTES),
            },
            {
                matcher: regexMatcher,
                data: {
                    matcherPattern: `^((.*)-)?(${SportConstant.Golf})/{eachWay}$`,
                    matcherMode: 'full',
                },
                loadChildren: () => import('../racing/racing.routes').then((r) => r.RACING_ROUTES),
            },
            {
                matcher: regexMatcher,
                data: {
                    matcherPattern: `^((.*)-)?(${SportConstant.Horses})$`,
                },
                loadChildren: () => import('../racing/racing.routes').then((r) => r.RACING_ROUTES),
            },
            {
                matcher: regexMatcher,
                data: {
                    matcherPattern: `^((.*)-)?(${SportConstant.Greyhounds})$`,
                },
                loadChildren: () => import('../racing/racing.routes').then((r) => r.RACING_ROUTES),
            },
            // sport routes
            {
                matcher: regexMatcher,
                canActivateChild: [SportRedirectGuard],
                data: {
                    matcherPattern: String.raw`^((.*)-)?(\d+)$`,
                    matcherMapping: {
                        sportName: 2,
                        sport: 3,
                    },
                    subNavigation: SportSubNavigationResolve,
                    sitemap: SitemapRoutingToken.SportSitemapResolver,
                },
                loadChildren: () => import('../event-list/event-list.routes').then((r) => r.EVENT_LIST_ROUTES),
            },
            {
                path: '{calendar}',
                loadComponent: () => import('../calendar/calendar.component').then((c) => c.CalendarComponent),
                resolve: use(CalendarResolver),
                data: {
                    breadcrumbs: CalendarBreadcrumbsResolver,
                    sitemap: SitemapRoutingToken.CalendarSitemapResolver,
                },
            },
            {
                matcher: regexMatcher,
                data: {
                    matcherPattern: '^({in30minutes}|{in60minutes}|{in180minutes}|{today}|{tomorrow}|{after2days}|{after3days})$',
                    matcherMapping: {
                        context: 1,
                    },
                    subNavigationHiddenOnSmallScreen: true,
                    breadcrumbs: CalendarBreadcrumbsResolver,
                    sitemap: SitemapRoutingToken.CalendarSitemapResolver,
                },
                loadComponent: () => import('../sport-list/sport-list-overview.component').then((c) => c.SportListOverviewComponent),
                resolve: use(SportListOverviewResolver),
            },
            {
                path: '{multiBuilder}',
                loadChildren: () => import('../multi-builder/multi-builder.routes').then((r) => r.MULTI_BUILDER_ROUTES),
                data: { shouldKeepSubNav: true },
            },
            {
                path: '{standings}',
                children: [
                    {
                        path: '',
                        loadComponent: () => import('@frontend/sports/common/ui/empty-component').then((c) => c.EmptyComponent),
                    },
                    {
                        matcher: multiNameIdentifierMatcher,
                        data: {
                            matcherParams: ['sport', 'region', 'league', 'group'],
                            sitemap: SitemapRoutingToken.StandingsSitemapResolver,
                        },
                        loadComponent: () => import('../standings-page/standings-page.component').then((c) => c.StandingsPageComponent),
                        resolve: use(StandingsPageResolver),
                    },
                ],
            },
            {
                path: '{bets}',
                loadChildren: () => import('../event-list/event-list.routes').then((r) => r.EVENT_BETTING_ROUTES),
            },
            {
                path: '{esports}',
                children: [...INTERNAL_ESPORTS_ROUTES],
            },
        ],
    },
];

export const EXTRA_SPORTS_ROUTES: SportRoute[] = [
    {
        path: 'news',
        children: [
            {
                path: '**',
                loadComponent: () => import('@frontend/sports/common/ui/empty-component').then((c) => c.EmptyComponent),
                canActivate: [OutsideSportsGuard],
            },
        ],
    },
];

export const SECONDARY_SPORTS_ROUTES: SportRoute[] = [
    {
        path: '{events}',
        loadChildren: () => import('../event-details/modal/event-details-modal.routes').then((t) => t.EVENT_DETAILS_MODAL_ROUTES),
    },
];
